import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { TranslationKeys } from '../../../../translations';
import formatter from '../../../../utils/formatter';
import { ActionLink } from '../../../../components';
import type { PensioenbijMutation } from '../../../../hooks/api/tools';
import {
  getPensioenbijAutoValueTranslationId,
  getPensioenbijFieldLabelTranslationId,
  getPensioenbijMutationStatusTranslationId,
} from './pensioenbijAutoValueTranslation';

const kebabToCamel = (s: string) => s.replaceAll(/-([a-z])/g, (_: string, c: string) => c.toUpperCase());
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const formatFieldLabel = (key: string) => {
  const normalized = key
    .replaceAll(/^@/g, '')
    .replaceAll(/([A-Z])/g, ' $1')
    .replaceAll('_', ' ')
    .replaceAll(/\s+/g, ' ')
    .trim();

  if (!normalized) return key;

  return normalized
    .replaceAll(/^./g, char => char.toUpperCase())
    .replaceAll(/\bK V K\b/g, 'KVK')
    .replaceAll(/\bI D\b/g, 'ID');
};

const isUuidValue = (value: unknown) => typeof value === 'string' && UUID_PATTERN.test(value.trim());

const DATE_FIELDS = new Set([
  'createdAt',
  'updatedAt',
  'processingDate',
  'employeeDateOfBirth',
  'employeeDateOfEmployment',
  'employeeDateOfEmploymentEnd',
]);

const toDisplayValue = (field: string, value: unknown) => {
  if (value === null || value === undefined || value === '') return '-';

  if (DATE_FIELDS.has(field) && typeof value === 'string') {
    return formatter.date(value);
  }

  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'bigint') return value.toString();
  if (typeof value === 'symbol') return value.toString();
  if (typeof value === 'function') return value.name ? `[Function: ${value.name}]` : '[Function]';
  if (typeof value === 'object') return JSON.stringify(value);

  return '-';
};

type Props = {
  mutation: PensioenbijMutation | null;
  onClose: () => void;
};
const STATUS_COLOR_MAP: Record<string, 'warning' | 'error' | 'success' | 'default'> = {
  pending: 'warning',
  failed: 'error',
  complete: 'success',
  sent: 'success',
};

const SYNC_STATUS_COMPLETED = 'complete';
const SYNC_STATUS_FAILED = 'failed';
const LabelCell = ({ children }: { children: React.ReactNode }) => (
  <TableCell
    sx={{
      fontWeight: 600,
      width: '40%',
      borderBottom: 'none',
      py: 1,
      pl: 0,
      color: 'text.secondary',
      verticalAlign: 'top',
    }}
  >
    {children}
  </TableCell>
);
const ValueCell = ({ children }: { children: React.ReactNode }) => (
  <TableCell sx={{ borderBottom: 'none', py: 1 }}>{children}</TableCell>
);

const DynamicFieldRow = ({ field, value }: { field: string; value: unknown }) => {
  const translationId = getPensioenbijAutoValueTranslationId(field, value);
  const labelTranslationId = getPensioenbijFieldLabelTranslationId(field);

  return (
    <TableRow>
      <LabelCell>
        <Typography variant="body2" color="text.primary">
          {labelTranslationId ? <FormattedMessage id={labelTranslationId} /> : formatFieldLabel(field)}
        </Typography>
      </LabelCell>
      <ValueCell>
        <Typography variant="body2" color="text.primary" sx={{ wordBreak: 'break-all' }}>
          {translationId ? <FormattedMessage id={translationId} /> : toDisplayValue(field, value)}
        </Typography>
      </ValueCell>
    </TableRow>
  );
};

export const PensioenbijMutationDetailModal = ({ mutation, onClose }: Props) => {
  const additionalFields = React.useMemo(() => {
    if (!mutation) return [] as Array<[string, unknown]>;

    const alreadyRendered = new Set([
      '@id',
      '@type',
      'externalId',
      'syncStatus',
      'mutationType',
      'employerName',
      'employerId',
      'employeeFirstName',
      'employeeLastName',
      'employeeRefID',
      'createdAt',
      'processingDate',
      'relatedEvent',
      'error',
      'syncMessages',
    ]);

    const alwaysExcludedUuidFields = new Set(['connectionId', 'batchId', 'instanceId', 'employeeId', 'relatedEventId']);

    const seenNormalizedFields = new Set<string>();

    return Object.entries(mutation).filter(([field, value]) => {
      if (alreadyRendered.has(field)) return false;
      if (alwaysExcludedUuidFields.has(field)) return false;
      if (isUuidValue(value)) return false;

      const normalizedField = field.toLowerCase();
      if (seenNormalizedFields.has(normalizedField)) return false;
      seenNormalizedFields.add(normalizedField);

      return true;
    });
  }, [mutation]);

  const relatedEventAdditionalFields = React.useMemo(() => {
    if (!mutation?.relatedEvent) return [] as Array<[string, unknown]>;

    const alreadyRendered = new Set(['@id', '@type', 'eventType', 'eventDate', 'state', 'createdAt']);
    const seenNormalizedFields = new Set<string>();

    return Object.entries(mutation.relatedEvent).filter(([field]) => {
      if (alreadyRendered.has(field)) return false;

      const normalizedField = field.toLowerCase();
      if (seenNormalizedFields.has(normalizedField)) return false;
      seenNormalizedFields.add(normalizedField);

      return true;
    });
  }, [mutation]);

  if (!mutation) return null;

  const statusTranslationId = getPensioenbijMutationStatusTranslationId(mutation.syncStatus);
  const statusColor = STATUS_COLOR_MAP[mutation.syncStatus] ?? 'default';
  const employeeName = mutation.employeeFirstName + (mutation.employeeLastName ? ' ' + mutation.employeeLastName : '');
  const syncMessagesContent = mutation.syncMessages || mutation.error;
  const hasSyncMessages = Boolean(syncMessagesContent) && syncMessagesContent !== '-';
  const isCompleted = mutation.syncStatus === SYNC_STATUS_COMPLETED;
  const isFailed = mutation.syncStatus === SYNC_STATUS_FAILED;

  return (
    <Dialog open={Boolean(mutation)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <FormattedMessage id={TranslationKeys.pensioenbij_mutation_detail_title} />
      </DialogTitle>
      <DialogContent dividers>
        <Table size="small">
          <TableBody>
            <TableRow>
              <LabelCell>
                <FormattedMessage id={TranslationKeys.pensioenbij_mutation_detail_status} />
              </LabelCell>
              <ValueCell>
                <Chip
                  label={statusTranslationId ? <FormattedMessage id={statusTranslationId} /> : mutation.syncStatus}
                  color={statusColor}
                  size="small"
                />
              </ValueCell>
            </TableRow>
            <TableRow>
              <LabelCell>
                <FormattedMessage id={TranslationKeys.pensioenbij_mutation_detail_mutationType} />
              </LabelCell>
              <ValueCell>
                <FormattedMessage
                  id={
                    TranslationKeys[`events_type_${mutation.mutationType}` as keyof typeof TranslationKeys] ??
                    TranslationKeys.global_selectUnknown
                  }
                />
              </ValueCell>
            </TableRow>
            <TableRow>
              <LabelCell>
                <FormattedMessage id={TranslationKeys.pensioenbij_mutation_detail_employeeName} />
              </LabelCell>
              <ValueCell>
                {mutation.employerId && mutation.employeeRefID ? (
                  <ActionLink to={`/employers/${mutation.employerId}/employees/${mutation.employeeRefID}`}>
                    {employeeName}
                  </ActionLink>
                ) : (
                  <span>{employeeName}</span>
                )}
              </ValueCell>
            </TableRow>
            <TableRow>
              <LabelCell>
                <FormattedMessage id={TranslationKeys.global_employer} />
              </LabelCell>
              <ValueCell>
                {mutation.employerId ? (
                  <ActionLink to={`/employers/${mutation.employerId}`}>{mutation.employerName}</ActionLink>
                ) : (
                  <span>{mutation.employerName}</span>
                )}
              </ValueCell>
            </TableRow>
            <TableRow>
              <LabelCell>
                <FormattedMessage id={TranslationKeys.pensioenbij_mutation_detail_creationDate} />
              </LabelCell>
              <ValueCell>{formatter.date(mutation.createdAt)}</ValueCell>
            </TableRow>
            {mutation.processingDate && (
              <TableRow>
                <LabelCell>
                  <FormattedMessage id={TranslationKeys.pensioenbij_mutation_detail_processingDate} />
                </LabelCell>
                <ValueCell>{formatter.date(mutation.processingDate)}</ValueCell>
              </TableRow>
            )}
            {additionalFields.map(([field, value]) => (
              <DynamicFieldRow key={field} field={field} value={value} />
            ))}
          </TableBody>
        </Table>
        {mutation.relatedEvent && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              <FormattedMessage id={TranslationKeys.pensioenbij_mutation_detail_relatedEvent} />
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <LabelCell>
                    <FormattedMessage id={TranslationKeys.pensioenbij_mutation_detail_eventType} />
                  </LabelCell>
                  <ValueCell>
                    <FormattedMessage
                      id={
                        TranslationKeys[
                          `events_type_${kebabToCamel(mutation.relatedEvent.eventType)}` as keyof typeof TranslationKeys
                        ] ?? mutation.relatedEvent.eventType
                      }
                    />
                  </ValueCell>
                </TableRow>
                <TableRow>
                  <LabelCell>
                    <FormattedMessage id={TranslationKeys.pensioenbij_mutation_detail_eventDate} />
                  </LabelCell>
                  <ValueCell>{formatter.date(mutation.relatedEvent.eventDate)}</ValueCell>
                </TableRow>
                <TableRow>
                  <LabelCell>
                    <FormattedMessage id={TranslationKeys.pensioenbij_mutation_detail_eventState} />
                  </LabelCell>
                  <ValueCell>
                    <FormattedMessage
                      id={
                        TranslationKeys[
                          mutation.relatedEvent.state.replaceAll('.', '_') as keyof typeof TranslationKeys
                        ] ?? mutation.relatedEvent.state
                      }
                    />
                  </ValueCell>
                </TableRow>
                <TableRow>
                  <LabelCell>
                    <FormattedMessage id={TranslationKeys.pensioenbij_mutation_detail_creationDate} />
                  </LabelCell>
                  <ValueCell>{formatter.date(mutation.relatedEvent.createdAt)}</ValueCell>
                </TableRow>
                {relatedEventAdditionalFields.map(([field, value]) => (
                  <DynamicFieldRow key={field} field={field} value={value} />
                ))}
              </TableBody>
            </Table>
          </>
        )}
        {hasSyncMessages && isCompleted && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom color="success.main" sx={{ fontWeight: 600 }}>
              <FormattedMessage id={TranslationKeys.pensioenbij_mutation_detail_response} />
            </Typography>
            <Typography
              variant="body2"
              sx={{
                background: theme => theme.palette.grey[100],
                p: 1.5,
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {syncMessagesContent}
            </Typography>
          </>
        )}
        {hasSyncMessages && isFailed && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom color="error" sx={{ fontWeight: 600 }}>
              <FormattedMessage id={TranslationKeys.pensioenbij_mutation_detail_error} />
            </Typography>
            <Typography
              variant="body2"
              sx={{
                background: theme => theme.palette.grey[100],
                p: 1.5,
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {syncMessagesContent}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          <FormattedMessage id={TranslationKeys.global_close} />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
